import { useMemo } from 'react';
import { t } from '@/lib/i18n';
import { formatNumber, formatElapsedTime } from '@/lib/format';
import type { Member } from '@/features/members/schemas';
import type { JobListItem } from '@/features/jobs/schemas';
import { DashboardGreeting } from '../components/DashboardGreeting';
import { StatTile } from '../components/StatTile';
import { TeamRosterMini, memberStatus } from '../components/TeamRosterMini';
import { ActivityFeed } from '../components/ActivityFeed';
import styles from './PopulatedDashboard.module.css';

export interface PopulatedDashboardProps {
  firstName: string;
  members: Member[];
  jobs: JobListItem[];
}

export function PopulatedDashboard({ firstName, members, jobs }: PopulatedDashboardProps) {
  const kindByMemberId = useMemo(() => new Map(members.map((m) => [m.id, m.kind])), [members]);

  const teamSize = members.length;
  const idleCount = members.filter((m) => memberStatus(m, jobs) === 'idle').length;
  const jobsRunning = jobs.filter((j) => j.status === 'running').length;
  const jobsCompleted = jobs.filter((j) => j.status === 'completed').length;
  const needsInput = 0; // spec §8.1 — not yet modelled in the API

  const longestRunningJob = useMemo(() => {
    const running = jobs
      .filter((j) => j.status === 'running' && j.started_at)
      .sort((a, b) => Date.parse(a.started_at!) - Date.parse(b.started_at!));
    return running[0] ?? null;
  }, [jobs]);

  const lastCompletedJob = useMemo(() => {
    return (
      [...jobs]
        .filter((j) => j.status === 'completed')
        .sort((a, b) => Date.parse(b.completed_at ?? '0') - Date.parse(a.completed_at ?? '0'))[0] ??
      null
    );
  }, [jobs]);

  const firstWaitingJob = jobs.find((j) => j.status === 'awaiting_input') ?? null;

  const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const completedThisWeek = jobs.filter(
    (j) => j.status === 'completed' && Date.parse(j.completed_at ?? '0') > oneWeekAgo,
  ).length;

  return (
    <div className={styles.root} data-testid="dashboard-state-populated">
      <DashboardGreeting
        state="populated"
        firstName={firstName}
        runningJobs={jobsRunning}
        needsInputCount={needsInput}
      />
      <section className={styles.metricsSection}>
        <h2 className={styles.metricsTitle}>{t('Current Activity')}</h2>
        <div className={styles.tileRow}>
          <StatTile
            label={t('Team Members')}
            value={formatNumber(teamSize)}
            subtitle={idleCount === 0 ? t('all active') : t('{n} idle', { n: idleCount })}
            badge={{ label: t('{n} total', { n: formatNumber(teamSize) }), tone: 'neutral' }}
            detail={idleCount === 0 ? t('All active') : t('{n} idle', { n: idleCount })}
          />
          <StatTile
            label={t('Jobs Running')}
            value={formatNumber(jobsRunning)}
            subtitle={t('live work in progress')}
            tone={jobsRunning > 0 ? 'action' : 'default'}
            badge={{
              label: jobsRunning > 0 ? t('In progress') : t('None running'),
              tone: jobsRunning > 0 ? 'action' : 'neutral',
            }}
            detail={
              longestRunningJob
                ? t('Longest: {name} · {elapsed}', {
                    name: longestRunningJob.assigned_member.display_name,
                    elapsed: formatElapsedTime(longestRunningJob.started_at!),
                  })
                : t('All quiet')
            }
          />
          <StatTile
            label={t('Need Your Input')}
            value={formatNumber(needsInput)}
            subtitle={t('members blocked on you')}
            tone={needsInput > 0 ? 'danger' : 'default'}
            badge={{
              label: needsInput > 0 ? t('Action needed') : t('All clear'),
              tone: needsInput > 0 ? 'danger' : 'success',
            }}
            detail={
              firstWaitingJob
                ? t('From: {name} · {title}', {
                    name: firstWaitingJob.assigned_member.display_name,
                    title: firstWaitingJob.title,
                  })
                : undefined
            }
          />
          <StatTile
            label={t('Jobs Completed')}
            value={formatNumber(jobsCompleted)}
            subtitle={t('across the team, all time')}
            badge={{
              label:
                completedThisWeek > 0
                  ? t('↑ {n} this week', { n: completedThisWeek })
                  : t('All time'),
              tone: completedThisWeek > 0 ? 'success' : 'neutral',
            }}
            detail={
              lastCompletedJob
                ? t('Last: {title}', { title: lastCompletedJob.title })
                : t('No jobs yet')
            }
          />
        </div>
      </section>
      <div className={styles.twoCol}>
        <TeamRosterMini members={members} jobs={jobs} />
        <ActivityFeed jobs={jobs} kindByMemberId={kindByMemberId} />
      </div>
    </div>
  );
}
